import type {
  BusinessSignupRequest,
  BusinessSignupResponse,
  BusinessVerifyRequest,
  BusinessVerifyResponse,
  BusinessSignInRequest,
  AuthenticatedBusinessSignIn,
  ResendVerificationRequest,
  ResendVerificationResponse,
} from './authTypes';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type { StoredSessionData } from '@/services/auth/types';
import { calculateTokenExpiry } from '@/lib';
import { parseCognitoIdToken } from '@/services/auth/cognito-id-token';

// Async thunk to restore session on app load
export const restoreSession = createAppAsyncThunk<StoredSessionData>(
  'auth/restoreSession',
  async (_, { extra: { config } }) => {
    const { sessionStore, logger } = config;
    const storedData = sessionStore.loadTokens();

    const parsedClaims = storedData.idToken
      ? parseCognitoIdToken(storedData.idToken)
      : { authRole: null, authUserType: null };
    logger?.debug('[restoreSession] Reparsed UI-routing claims from ID token', parsedClaims);

    const updatedData: StoredSessionData = {
      ...storedData,
      authRole: parsedClaims.authRole,
      authUserType: parsedClaims.authUserType,
    };
    if (storedData.authRole !== updatedData.authRole || storedData.authUserType !== updatedData.authUserType) {
      sessionStore.saveTokens(updatedData);
    }
    return updatedData;
  },
);

// Async thunk for user logout
export const logoutUser = createAppAsyncThunk<void>('auth/logout', async (_, { extra: { config } }) => {
  const { sessionStore } = config;
  sessionStore.clear();
});

// Business registration thunks
export const businessSignup = createAppAsyncThunk<BusinessSignupResponse, BusinessSignupRequest>(
  'auth/businessSignup',
  async (request, { extra: { config }, rejectWithValue }) => {
    const response = await config.authService.signup(request);

    if (!response.success) {
      return rejectWithValue(response);
    }

    return response;
  },
);

export const businessVerify = createAppAsyncThunk<BusinessVerifyResponse, BusinessVerifyRequest>(
  'auth/businessVerify',
  async (request, { extra: { config }, rejectWithValue }) => {
    const response = await config.authService.verifySignup(request);

    if (!response.success) {
      return rejectWithValue(response);
    }

    return response;
  },
);

// Business sign-in thunk
export const businessSignIn = createAppAsyncThunk<AuthenticatedBusinessSignIn, BusinessSignInRequest>(
  'auth/businessSignIn',
  async (request, { extra: { config }, rejectWithValue }) => {
    const { authService, sessionStore, logger } = config;
    const { email } = request;
    const response = await authService.signIn(request);

    if (!response.success || !response.tokens) {
      return rejectWithValue(response);
    }

    const { userId, authRole, authUserType } = parseCognitoIdToken(response.tokens.idToken);

    if (!userId) return rejectWithValue({ message: 'User ID missing in sign-in ID token' });

    const expiresAt = calculateTokenExpiry(response.tokens.expiresIn);
    logger?.debug('[businessSignIn] Parsed UI-routing claims from ID token', { authRole, authUserType });

    // Save tokens on successful sign-in
    logger?.debug('[businessSignIn] Saving tokens to session store', { authRole, authUserType });
    sessionStore.saveTokens({
      accessToken: response.tokens.accessToken,
      refreshToken: response.tokens.refreshToken,
      idToken: response.tokens.idToken,
      email,
      userId,
      expiresAt,
      authRole,
      authUserType,
    });
    logger?.debug('[businessSignIn] Tokens saved', { expiresAt });

    // Return response with extracted values for Redux state
    return {
      ...response,
      userId,
      authRole,
      authUserType,
    };
  },
);

// Resend verification code thunk
export const businessResendVerification = createAppAsyncThunk<ResendVerificationResponse, ResendVerificationRequest>(
  'auth/businessResendVerification',
  async (request, { extra: { config }, rejectWithValue }) => {
    const response = await config.authService.resendVerification(request);

    if (!response.success) {
      return rejectWithValue(response);
    }

    return response;
  },
);
